import type { TFunction } from 'i18next';
import { isAppError } from '@/lib/api/errors';
import type { MediaKind, WriteWarning } from '@/lib/api/types';

/**
 * The jobs a media asset is given on the public site (be-tepco `media.Role`). The server checks
 * each assignment and refuses or warns with the codes below; this module turns those codes into
 * the console's own words, in English and Arabic, instead of showing the server's English text.
 */
export type MediaRole = 'logo' | 'favicon' | 'share_image';

/** What the picker offers for each role (SVG has no share-image renditions, so not there). */
export const ROLE_KINDS: Record<MediaRole, readonly MediaKind[]> = {
  logo: ['image', 'svg'],
  favicon: ['image', 'svg'],
  share_image: ['image'],
};

const ERROR_CODES = new Set([
  'not_found',
  'restricted',
  'not_an_image',
  'svg_not_allowed',
  'not_square',
  'too_small',
  'file_missing',
]);

const WARNING_CODES = new Set(['aspect_ratio', 'low_resolution', 'no_transparency']);

/** Fields whose 422s and warnings carry media-role codes. */
const ROLE_OF_FIELD: Record<string, MediaRole> = {
  brand_logo_id: 'logo',
  brand_logo_ar_id: 'logo',
  brand_logo_mono_id: 'logo',
  favicon_id: 'favicon',
  'default_seo.og_image_id': 'share_image',
  'seo.og_image_id': 'share_image',
};

/** The role a request field assigns, if it is a brand or share field. */
export function roleOfField(field: string): MediaRole | undefined {
  return ROLE_OF_FIELD[field];
}

/**
 * The validation errors of a refused save, with every media-role refusal reworded in the
 * console's language. Other fields keep the message the form would have shown.
 */
export function localizeRoleErrors(
  error: unknown,
  fields: Record<string, string>,
  t: TFunction,
): Record<string, string> {
  if (!isAppError(error)) return fields;
  const out = { ...fields };
  for (const d of error.details ?? []) {
    const role = roleOfField(d.field);
    if (role && d.code && ERROR_CODES.has(d.code)) {
      out[d.field] = t(`mediaRole.error.${d.code}`, { context: role });
    }
  }
  return out;
}

/** One warning in the console's language (unknown codes keep the server's text). */
export function warningText(t: TFunction, w: WriteWarning): string {
  return WARNING_CODES.has(w.code) ? t(`mediaRole.warning.${w.code}`) : w.message;
}

/** Warnings grouped by field, for inline display under each picker. */
export function warningsByField(warnings: readonly WriteWarning[]): Record<string, WriteWarning[]> {
  const out: Record<string, WriteWarning[]> = {};
  for (const w of warnings) (out[w.field] ??= []).push(w);
  return out;
}
